import { html, render } from "lit-html";
import { fetchFrigorifico, mapResultado } from "./data/api.js";
import { translations, languages } from "./data/translations.js";

class SifSearchApp {
  constructor() {
    this.searchText = "";
    this.searchResults = [];
    this.isSearching = false;
    this.searchTimeout = null;
    this.searchRequestId = 0;
    this.currentLanguage = localStorage.getItem("language") || "pt";
    this.selectedCode = null;
    this.companyInfo = null;
    this.showQrCode = false;

    // Bind dos métodos para manter o contexto
    this.handleSearchInput = this.handleSearchInput.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.performSearch = this.performSearch.bind(this);
    this.clearSearch = this.clearSearch.bind(this);
    this.fillExampleCode = this.fillExampleCode.bind(this);
    this.changeLanguage = this.changeLanguage.bind(this);
    this.showCompanyInfo = this.showCompanyInfo.bind(this);
    this.closeCompanyInfo = this.closeCompanyInfo.bind(this);
    this.openShareModal = this.openShareModal.bind(this);
    this.closeShareModal = this.closeShareModal.bind(this);
  }

  handleSearchInput(event) {
    this.searchText = event.target.value;

    // Limpar timeout anterior
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Executar busca com delay (debounce)
    this.searchTimeout = setTimeout(() => {
      this.performSearch();
    }, 1500);

    // Atualizar a tela imediatamente para mostrar o texto digitado
    this.render();
  }

  handleKeyPress(event) {
    console.log("Tecla pressionada:", event.key); // Debug
    if (event.key === "Enter") {
      event.preventDefault();
      // Cancelar o debounce e executar imediatamente
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      // this.performSearch();
    }
  }

  performSearch() {
    console.log("Executando pesquisa para:", this.searchText); // Debug

    if (!this.searchText || this.searchText.trim() === "") {
      this.searchResults = [];
      this.render();
      return;
    }

    this.isSearching = true;
    const requestId = ++this.searchRequestId;

    //ocultar teclado em dispositivos móveis
    const input = document.querySelector("#search-input");
    if (input) {
      input.blur();
    }

    this.render();

    // Buscar dados na API
    fetchFrigorifico(this.searchText)
      .then((data) => {
        // Ignorar resposta desatualizada (usuário pesquisou novamente)
        if (requestId !== this.searchRequestId) return;

        this.searchResults = data && data.encontrado ? data.resultados : [];
        console.log("Resultados encontrados:", this.searchResults.length); // Debug
      })
      .catch((error) => {
        if (requestId !== this.searchRequestId) return;

        console.error("Erro na busca:", error);
        this.searchResults = [];
      })
      .finally(() => {
        if (requestId !== this.searchRequestId) return;

        this.isSearching = false;
        this.render();
      });
  }

  clearSearch() {
    this.searchText = "";
    this.searchResults = [];
    this.render();

    // Focar novamente no input
    const input = document.querySelector("#search-input");
    if (input) {
      input.focus();
    }
  }

  // Função para preencher o código de exemplo no campo de busca
  fillExampleCode(code) {
    this.searchText = code;
    this.performSearch();

    // Focar no input após preencher
    setTimeout(() => {
      const input = document.querySelector("#search-input");
      if (input) {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }, 150);
  }

  // Função para trocar idioma
  changeLanguage(languageCode) {
    this.currentLanguage = languageCode;
    localStorage.setItem("language", languageCode);
    this.render();

    // Atualizar direção do texto para árabe
    const body = document.body;
    if (languageCode === "ar") {
      body.setAttribute("dir", "rtl");
      body.classList.add("rtl");
    } else {
      body.setAttribute("dir", "ltr");
      body.classList.remove("rtl");
    }
  }

  // Função para obter texto traduzido
  t(key) {
    return (
      translations[this.currentLanguage][key] || translations["pt"][key] || key
    );
  }

  // Função para mostrar informações da empresa
  showCompanyInfo(resultado) {
    this.selectedCode = resultado.codigo;
    this.companyInfo = [mapResultado(resultado)];
    this.render();
  }

  // Função para fechar modal de informações
  closeCompanyInfo() {
    this.selectedCode = null;
    this.companyInfo = null;
    this.render();
  }

  // Função para abrir modal de compartilhar
  openShareModal() {
    this.showQrCode = true;
    this.render();
  }

  // Função para fechar modal de compartilhar
  closeShareModal() {
    this.showQrCode = false;
    this.render();
  }

  render() {
    const template = html`
      <div class="sif-search-app">
        <div class="header">
          <img src="halal.png" alt="Halal Logo" class="header-logo" />
          <h1>${this.t("title")}</h1>
          <p class="subtitle">${this.t("subtitle")}</p>
        </div>

        <div class="language-selector">
          <span class="language-label">${this.t("languageLabel")}</span>
          <div class="language-options">
            ${languages.map(
              (lang) => html`
                <button
                  class="language-btn ${this.currentLanguage === lang.code
                    ? "active"
                    : ""}"
                  @click=${() => this.changeLanguage(lang.code)}
                  title="${lang.name}"
                >
                  ${lang.flag} ${lang.name}
                </button>
              `
            )}
          </div>
        </div>

        <div class="search-container">
          <div class="search-box">
            <input
              id="search-input"
              type="text"
              placeholder="${this.t("searchPlaceholder")}"
              .value=${this.searchText}
              @input=${this.handleSearchInput}
              @keydown=${this.handleKeyPress}
              class="search-input"
            />
            <div class="search-buttons">
              ${this.searchText
                ? html`
                    <button
                      @click=${this.clearSearch}
                      class="clear-button"
                      title="${this.t("clearSearch")}"
                    >
                      <img src="icons/close.svg" alt="" class="btn-icon" />
                    </button>
                  `
                : ""}
              <button
                @click=${this.performSearch}
                class="search-button"
                title="${this.t("searchButton")}"
              >
                <img src="icons/search.svg" alt="" class="btn-icon" />
              </button>
            </div>
          </div>

          ${this.isSearching
            ? html`
                <div class="loading">
                  <img src="icons/loader.svg" class="icon-spin" alt="" />
                  ${this.t("searching")}
                </div>
              `
            : ""}
        </div>

        <div class="results-container">
          ${this.searchText && !this.isSearching
            ? html`
                <div
                  class="halal-result ${this.searchResults.length > 0
                    ? "is-halal"
                    : "not-halal"}"
                >
                  <div class="halal-status">
                    <span class="halal-icon">
                      <img
                        src="${this.searchResults.length > 0
                          ? "icons/check.svg"
                          : "icons/cross.svg"}"
                        alt=""
                        class="status-icon"
                      />
                    </span>
                    <h2 class="halal-title">
                      ${this.searchResults.length > 0
                        ? this.t("isHalal")
                        : this.t("notHalal")}
                    </h2>
                  </div>
                  <p class="halal-message">
                    ${this.searchResults.length > 0
                      ? this.t("halalMessage")
                      : this.t("notHalalMessage")}
                  </p>
                  <p class="search-term">
                    ${this.t("resultsFor")}:
                    <strong>"${this.searchText}"</strong>
                  </p>

                  ${this.searchResults.length > 0
                    ? html`
                        <div class="found-codes">
                          <p class="found-codes-title">
                            ${this.t("foundCodes")}
                          </p>
                          <p class="click-hint">${this.t("clickForDetails")}</p>
                          <div class="results-list">
                            ${this.searchResults.map(
                              (resultado) => html`
                                <div
                                  class="result-item"
                                  @click=${() =>
                                    this.showCompanyInfo(resultado)}
                                >
                                  <span class="result-text">
                                    <span class="code-badge"
                                      >${resultado.codigo}</span
                                    >
                                    <span class="company-name"
                                      >${resultado.nome}</span
                                    >
                                  </span>
                                  <span class="click-icon"
                                    ><img
                                      src="icons/info.svg"
                                      alt=""
                                      class="info-icon"
                                  /></span>
                                </div>
                              `
                            )}
                          </div>
                        </div>
                      `
                    : html`
                        <div class="help-section">
                          <p class="help-text">${this.t("helpTitle")}</p>
                          <ul class="help-list">
                            <li>${this.t("helpSifNumber")}</li>
                            <li>${this.t("helpCompleteCode")}</li>
                            <li>${this.t("helpEstablishment")}</li>
                          </ul>
                        </div>
                      `}
                </div>
              `
            : this.searchText === ""
            ? html`
                <div class="welcome-message">
                  <p>${this.t("welcomeMessage")}</p>
                  <div class="example-codes">
                    <p>
                      <strong>${this.t("examplesTitle")}</strong>
                    </p>
                    <div class="code-examples">
                      <span
                        class="example-code"
                        @click=${() => this.fillExampleCode("SIF 2258")}
                        >SIF 2258</span
                      >
                      <span
                        class="example-code"
                        @click=${() => this.fillExampleCode("SIF 3941")}
                        >SIF 3941</span
                      >
                      <span
                        class="example-code"
                        @click=${() => this.fillExampleCode("SIF 1440")}
                        >SIF 1440</span
                      >
                      <span
                        class="example-code"
                        @click=${() => this.fillExampleCode("SIF 89")}
                        >SIF 89</span
                      >
                    </div>
                    <div class="more-examples">
                      <p><strong>${this.t("moreExamplesTitle")}</strong></p>
                      <div class="search-tips">
                        <span
                          class="tip-code"
                          @click=${() => this.fillExampleCode("2258")}
                          >${this.t("tipNumbers")}</span
                        >
                        <span
                          class="tip-code"
                          @click=${() => this.fillExampleCode("SIF")}
                          >${this.t("tipType")}</span
                        >
                        <span
                          class="tip-code"
                          @click=${() => this.fillExampleCode("SIF 3941")}
                          >${this.t("tipOrgan")}</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              `
            : ""}
        </div>

        ${this.selectedCode && this.companyInfo
          ? html`
              <div class="modal-overlay" @click=${this.closeCompanyInfo}>
                <div class="modal-content" @click=${(e) => e.stopPropagation()}>
                  <button class="modal-close" @click=${this.closeCompanyInfo}>
                    <img src="icons/close.svg" alt="" class="btn-icon-sm" />
                  </button>
                  <div class="modal-header">
                    <h2>
                      <img
                        src="icons/clipboard.svg"
                        alt=""
                        class="header-icon"
                      />
                      ${this.selectedCode}
                    </h2>
                    <span class="halal-badge"
                      ><img src="icons/check.svg" alt="" class="badge-icon" />
                      HALAL</span
                    >
                  </div>
                  <div class="company-info-list">
                    ${this.companyInfo.map(
                      (company) => html`
                        <div class="company-card">
                          <div class="info-row">
                            <span class="info-label">
                              <img
                                src="icons/building.svg"
                                alt=""
                                class="label-icon"
                              />
                              ${this.t("companyName")}:
                            </span>
                            <span class="info-value"
                              >${company.companyName}</span
                            >
                          </div>
                          <div class="info-row">
                            <span class="info-label">
                              <img
                                src="icons/location.svg"
                                alt=""
                                class="label-icon"
                              />
                              ${this.t("address")}:
                            </span>
                            <span class="info-value">${company.address}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label">
                              <img
                                src="icons/globe.svg"
                                alt=""
                                class="label-icon"
                              />
                              ${this.t("country")}:
                            </span>
                            <span class="info-value">${company.country}</span>
                          </div>
                          ${company.products
                            ? html`
                                <div class="info-row">
                                  <span class="info-label">
                                    <img
                                      src="icons/box.svg"
                                      alt=""
                                      class="label-icon"
                                    />
                                    ${this.t("products")}:
                                  </span>
                                  <span class="info-value"
                                    >${company.products}</span
                                  >
                                </div>
                              `
                            : ""}
                          ${company.certificateNumber
                            ? html`
                                <div class="info-row">
                                  <span class="info-label">
                                    <img
                                      src="icons/certificate.svg"
                                      alt=""
                                      class="label-icon"
                                    />
                                    ${this.t("certificate")}:
                                  </span>
                                  <span class="info-value"
                                    >${company.certificateNumber}</span
                                  >
                                </div>
                              `
                            : ""}
                          ${company.certifier
                            ? html`
                                <div class="info-row certifier">
                                  <span class="info-label">
                                    <img
                                      src="icons/medal.svg"
                                      alt=""
                                      class="label-icon"
                                    />
                                    ${this.t("certifier")}:
                                  </span>
                                  <span class="info-value certifier-text"
                                    >${company.certifier}</span
                                  >
                                </div>
                              `
                            : ""}
                          ${company.recognition
                            ? html`
                                <div class="info-row recognition">
                                  <span class="info-label">
                                    <img
                                      src="icons/medal.svg"
                                      alt=""
                                      class="label-icon"
                                    />
                                    ${this.t("recognition")}:
                                  </span>
                                  <span class="info-value recognition-text"
                                    >${company.recognition}</span
                                  >
                                </div>
                              `
                            : ""}
                        </div>
                      `
                    )}
                  </div>
                </div>
              </div>
            `
          : ""}
        ${this.showQrCode
          ? html`
              <div class="modal-overlay" @click=${this.closeShareModal}>
                <div
                  class="qr-modal-content"
                  @click=${(e) => e.stopPropagation()}
                >
                  <button class="modal-close" @click=${this.closeShareModal}>
                    <img src="icons/close.svg" alt="" class="btn-icon-sm" />
                  </button>
                  <div class="qr-modal-header">
                    <h2>${this.t("shareApp")}</h2>
                    <p>${this.t("scanQrCode")}</p>
                  </div>
                  <div class="qr-code-container">
                    <img
                      src="halal-qrcode.png"
                      alt="QR Code"
                      class="qr-code-image"
                    />
                  </div>
                </div>
              </div>
            `
          : ""}

        <footer class="app-footer">
          <button class="share-button" @click=${this.openShareModal}>
            <svg
              class="share-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            ${this.t("share")}
          </button>
        </footer>
      </div>
    `;

    render(template, document.querySelector("native-app"));
  }
}

// Inicializar a aplicação
const app = new SifSearchApp();
app.render();

// Focar no input quando a página carregar
window.addEventListener("DOMContentLoaded", () => {
  setTimeout(() => {
    const input = document.querySelector("#search-input");
    if (input) {
      input.focus();
    }
  }, 100);
});
